import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Settings, Plus, Trash2 } from "lucide-react";
import { useEquipamentos, useAdicionarEquipamentoProjeto, useProjetoEquipamentos, useRemoverEquipamentoProjeto } from "@/hooks/useEquipamentos";
import { toast } from "sonner";

interface EquipamentosSelectorProps {
  orcamentoId?: string;
  projetoId?: string;
  disabled?: boolean;
  clienteId?: number; // Para usar no cadastro de clientes
}

export function EquipamentosSelector({ orcamentoId, projetoId, disabled, clienteId }: EquipamentosSelectorProps) {
  const [dialogAberto, setDialogAberto] = useState(false);
  const [tipoSelecionado, setTipoSelecionado] = useState("painel");
  const [equipamentoSelecionado, setEquipamentoSelecionado] = useState("");
  const [quantidade, setQuantidade] = useState(1);
  const [precoCustom, setPrecoCustom] = useState("");
  const [observacoes, setObservacoes] = useState("");

  const { data: equipamentos } = useEquipamentos(tipoSelecionado);
  const { data: equipamentosProjeto } = useProjetoEquipamentos(projetoId, orcamentoId);
  const adicionarEquipamento = useAdicionarEquipamentoProjeto();
  const removerEquipamento = useRemoverEquipamentoProjeto();

  const equipamentosFiltrados = equipamentos?.filter(eq => eq.tipo === tipoSelecionado) || [];

  const handleAdicionarEquipamento = async () => {
    if (!equipamentoSelecionado) {
      toast.error("Selecione um equipamento");
      return;
    }

    if (quantidade <= 0) {
      toast.error("Quantidade deve ser maior que zero");
      return;
    }

    if (!projetoId && !orcamentoId) {
      toast.error("É necessário ter um projeto ou orçamento para adicionar equipamentos");
      return;
    }

    try {
      const equipamento = equipamentos?.find(eq => eq.id === equipamentoSelecionado);
      const preco = precoCustom ? parseFloat(precoCustom) : equipamento?.preco_unitario;

      await adicionarEquipamento.mutateAsync({
        projeto_id: projetoId,
        orcamento_id: orcamentoId,
        equipamento_id: equipamentoSelecionado,
        quantidade,
        preco_unitario: preco,
        observacoes: observacoes || undefined
      });

      toast.success("Equipamento adicionado com sucesso!");
      
      // Limpar form
      setEquipamentoSelecionado("");
      setQuantidade(1);
      setPrecoCustom("");
      setObservacoes("");
      setDialogAberto(false);
    } catch (error) {
      console.error('Erro ao adicionar equipamento:', error);
      toast.error("Erro ao adicionar equipamento");
    }
  };

  const handleRemoverEquipamento = async (equipamentoProjetoId: string) => {
    try {
      await removerEquipamento.mutateAsync(equipamentoProjetoId);
      toast.success("Equipamento removido com sucesso!");
    } catch (error) {
      console.error('Erro ao remover equipamento:', error);
      toast.error("Erro ao remover equipamento");
    }
  };

  const calcularTotal = () => {
    return equipamentosProjeto?.reduce((total, item) => {
      const preco = item.preco_unitario || item.equipamento?.preco_unitario || 0;
      return total + (preco * item.quantidade);
    }, 0) || 0;
  };

  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-gray-900">
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-green-600" />
            Equipamentos Selecionados
          </div>
          <Dialog open={dialogAberto} onOpenChange={setDialogAberto}>
            <DialogTrigger asChild>
              <Button 
                size="sm" 
                className="bg-primary hover:bg-primary/90"
                disabled={disabled}
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Equipamento
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>Selecionar Equipamentos</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <Tabs value={tipoSelecionado} onValueChange={setTipoSelecionado}>
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="painel">Painéis</TabsTrigger>
                    <TabsTrigger value="inversor">Inversores</TabsTrigger>
                    <TabsTrigger value="estrutura">Estruturas</TabsTrigger>
                    <TabsTrigger value="outros">Outros</TabsTrigger>
                  </TabsList>

                  <TabsContent value={tipoSelecionado} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Equipamento</Label>
                        <Select value={equipamentoSelecionado} onValueChange={setEquipamentoSelecionado}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione um equipamento" />
                          </SelectTrigger>
                          <SelectContent>
                            {equipamentosFiltrados.map((eq) => (
                              <SelectItem key={eq.id} value={eq.id}>
                                {eq.marca} - {eq.modelo} {eq.potencia && `(${eq.potencia})`}
                                {eq.preco_unitario && ` - R$ ${eq.preco_unitario.toFixed(2)}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Quantidade</Label>
                        <Input
                          type="number"
                          min="1"
                          value={quantidade}
                          onChange={(e) => setQuantidade(parseInt(e.target.value) || 1)}
                        />
                      </div>

                      <div>
                        <Label>Preço Unitário (opcional)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Deixe vazio para usar preço padrão"
                          value={precoCustom}
                          onChange={(e) => setPrecoCustom(e.target.value)}
                        />
                      </div>

                      <div>
                        <Label>Observações (opcional)</Label>
                        <Input
                          placeholder="Observações sobre este equipamento"
                          value={observacoes}
                          onChange={(e) => setObservacoes(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => setDialogAberto(false)}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        onClick={handleAdicionarEquipamento}
                        disabled={adicionarEquipamento.isPending}
                        className="bg-primary hover:bg-primary/90"
                      >
                        {adicionarEquipamento.isPending ? "Adicionando..." : "Adicionar"}
                      </Button>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </DialogContent>
          </Dialog>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!equipamentosProjeto || equipamentosProjeto.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Settings className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Nenhum equipamento selecionado</p>
            <p className="text-sm">Clique em "Adicionar Equipamento" para começar</p>
          </div>
        ) : (
          <div className="space-y-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead align="center">Tipo</TableHead>
                  <TableHead align="center">Equipamento</TableHead>
                  <TableHead align="center">Qtd</TableHead>
                  <TableHead align="center">Preço Unit.</TableHead>
                  <TableHead align="center">Total</TableHead>
                  <TableHead align="center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {equipamentosProjeto.map((item) => {
                  const preco = item.preco_unitario || item.equipamento?.preco_unitario || 0;
                  const total = preco * item.quantidade;
                  
                  return (
                    <TableRow key={item.id}>
                      <TableCell align="left" className="capitalize">{item.equipamento?.tipo}</TableCell>
                      <TableCell align="left">
                        <div>
                          <div className="font-medium">
                            {item.equipamento?.marca} - {item.equipamento?.modelo}
                          </div>
                          {item.equipamento?.potencia && (
                            <div className="text-sm text-gray-500">{item.equipamento.potencia}</div>
                          )}
                          {item.observacoes && (
                            <div className="text-sm text-gray-500">{item.observacoes}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell align="center">{item.quantidade}</TableCell>
                      <TableCell align="center">R$ {preco.toFixed(2)}</TableCell>
                      <TableCell align="center" className="font-medium">R$ {total.toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoverEquipamento(item.id)}
                          disabled={disabled || removerEquipamento.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <div className="flex justify-end pt-4 border-t">
              <div className="text-lg font-bold text-gray-900">
                Total dos Equipamentos: R$ {calcularTotal().toFixed(2)}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}